import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useInfiniteQuery, UseInfiniteQueryResult, useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { useForm, UseFormReturn } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useDebounce } from "../../../../hooks";
import { IOptionItem, MetadataI } from "../../../../typings/common";
import {
  deleteTrxCode,
  getTrxCode, getTrxCodeDetail, IGetTrxCodeData, postTrxCode,
  putTrxCode,
} from "../../../../services/trxCodeService";
import schema from "../schema";
import useAuth from "../../../../components/Authentication/useAuth";
import { CommonTrxTypes } from "../../../../enums/common";
import { getRangeNominal, IGetRangeNominalResponse } from "../../../../services/rangeNominalService";

export interface FilterI {
  search: string,
  typeCashflow?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

export interface TransactionCodeFormValuesI {
  trx_desc: string,
  sap_bktxt: string,
  gnlldr: string,
  sap_shkzg: string,
  range_code: string,
  lifnr: number,
  kunnr: number,
  prctr: number,
  idtrx1: number,
  idtrx2: number,
  idtrx3: number,
}

type ModalType = "CREATE" | "UPDATE" | "DELETE"
type ModalConfigType = { row?: IGetTrxCodeData, type: ModalType }
export type SetModalConfigType = Dispatch<SetStateAction<ModalConfigType | undefined>>

interface ContextValueI {
  methods: UseFormReturn<TransactionCodeFormValuesI, any, undefined | TransactionCodeFormValuesI>
  params: FilterI;
  setParams: Dispatch<SetStateAction<FilterI>>;
  meta: MetadataI;
  setMeta: Dispatch<SetStateAction<MetadataI>>;
  selectedEntryTypes: IOptionItem[],
  handleSelectEntryType: (opt: IOptionItem) => void,

  modalConfig?: ModalConfigType;
  setModalConfig: SetModalConfigType;
  detail?: IGetTrxCodeData;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  rangeCodes?: string[];

  transactionCodeQuery: UseInfiniteQueryResult<IGetTrxCodeData[] | never[], unknown>;
  transactionCodePages?: {
    pages: IGetTrxCodeData[][];
  };
  isFetching: boolean;
  isFetchingNextPage: boolean;
  refetchTable: () => void;
  fetchNextPage: () => void;
  rangeCodeQuery: UseInfiniteQueryResult<IGetRangeNominalResponse, unknown>;

  handleDelete: () => void;
  onSubmit: () => void;
}

export const Context = createContext<ContextValueI | undefined>(undefined);
export const useTransactionCode = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("useTransactionCode must be used within a Provider component");
  }

  return context;
};

const initialValues = {};

export const Provider = ({ children }: PropsWithChildren) => {
  const snackbar = useSnackbar();
  const { userInfo } = useAuth();
  const [params, setParams] = useState<FilterI>({
    search: "",
    sortBy: "trx_code",
    sortType: "DSC",
  });

  const methods = useForm<TransactionCodeFormValuesI>({
    defaultValues: initialValues,
    resolver: joiResolver(schema),
    mode: "all",
    shouldUnregister: true,
  });

  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [modalConfig, setModalConfig] = useState<ModalConfigType>();
  const [detail, setDetail] = useState<IGetTrxCodeData>();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [rangeCodes, setRangeCodes] = useState<string[]>([]);

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  const [selectedEntryTypes, setEntryTypes] = useState<IOptionItem[]>(CommonTrxTypes);
  const handleSelectEntryType = (itemSelected: IOptionItem) => {
    const newSelectedEntryTypes = selectedEntryTypes.map((item: IOptionItem) => {
      if (item.value === itemSelected.value) {
        return {
          ...item,
          checked: itemSelected.checked,
        };
      }
      return item;
    });
    setEntryTypes(newSelectedEntryTypes);
    setParams((curr) => ({
      ...curr,
      typeCashflow: newSelectedEntryTypes
        .reduce((prev, current) => {
          if (current.checked) return (prev ? `${prev},${current.value}` : `${current.value}`);
          return prev;
        }, ""),
    }));
  };

  const { isLoading: isDetailLoading, isLoadingError: isDetailLoadingError } = useQuery({
    queryKey: ["get_trx_code_detail", modalConfig?.row?.trx_code],
    enabled: !!modalConfig?.row?.trx_code && modalConfig.type === "UPDATE",
    queryFn: () => getTrxCodeDetail(modalConfig?.row?.trx_code || ""),
    onSuccess: (res) => {
      setDetail(res);
      methods.reset({
        gnlldr: res.gnlldr,
        range_code: res.range_code,
        sap_bktxt: res.sap_bktxt,
        sap_shkzg: res.sap_shkzg,
        trx_desc: res.trx_desc,
        idtrx1: Number(res.idtrx1),
        idtrx2: Number(res.idtrx2),
        idtrx3: Number(res.idtrx3),
        kunnr: Number(res.kunnr),
        lifnr: Number(res.lifnr),
        prctr: Number(res.prctr),
      });
    },
    onError: () => methods.reset({}),
  });

  useEffect(() => {
    if (!modalConfig) {
      setDetail(undefined);
      methods.reset({});
    }
  }, [modalConfig]);

  const fetchList = async (pageParam: number) => {
    const sortBy = params.sortType === "DEFAULT" ? "trx_code" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    return getTrxCode({
      search: params.search,
      type_cashflow: params.typeCashflow,
      sort_by: sortBy,
      sort_type: sortType,
      page_number: pageParam,
      page_size: 10,
    })
      .then((res) => {
        if (res.metadata) setMeta(res.metadata);
        return res.list_mst_trxcode;
      }).catch(() => []);
  };

  const transactionCodeQuery = useInfiniteQuery(
    ["transactionCodeList", params],
    async ({ queryKey, pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (meta.page < meta.total_pages ? meta.page + 1 : undefined),
      keepPreviousData: true,
    },
  );
  const {
    data: transactionCodePages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
  } = transactionCodeQuery;

  const rangeCodeQuery = useInfiniteQuery(
    ["rangeCodeList", params],
    async ({ queryKey, pageParam = 1 }) => (
      getRangeNominal({ page_number: pageParam, is_distinct: true }).then((res) => {
        setRangeCodes((curr) => Array.from(
          new Set([...curr, ...res.range.map((item) => item.range_code)]),
        ));
        return res;
      })
    ),
    {
      getNextPageParam: ({ metadata }) => (
        metadata.page < metadata.total_pages ? metadata.page + 1 : undefined
      ),
      keepPreviousData: true,
    },
  );

  const handleDelete = async () => {
    setLoading(true);
    if (modalConfig?.row?.trx_code) {
      deleteTrxCode(modalConfig.row?.trx_code).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          snackbar.show("Berhasil menghapus data", "success");
          setModalConfig(undefined);
        } else {
          snackbar.show(res.responseMessage, "error");
        }
      }).catch(() => {
        snackbar.show("Gagal menghapus data", "error");
      }).finally(() => setLoading(false));
    }
  };

  const onSubmit = methods.handleSubmit(async (val) => {
    setLoading(true);
    const isUpdate = modalConfig?.type === "UPDATE";
    const commonPayload = {
      ...val,
      is_active: true,
      lifnr: Boolean(val.lifnr),
      kunnr: Boolean(val.kunnr),
      prctr: Boolean(val.prctr),
      idtrx1: Boolean(val.idtrx1),
      idtrx2: Boolean(val.idtrx2),
      idtrx3: Boolean(val.idtrx3),
    };

    const resPromise = isUpdate && modalConfig.row?.trx_code
      ? putTrxCode({
        ...commonPayload,
        trx_code: modalConfig?.row?.trx_code,
        update_by: userInfo.preferred_username,
      })
      : postTrxCode({
        ...commonPayload,
        app_code: "CA",
        create_by: userInfo.preferred_username,
      });
    await resPromise.then((res) => {
      if (res.responseCode === "Succeed") {
        refetchTable();
        snackbar.show(`Berhasil ${isUpdate ? "mengubah" : "menambah"} data`, "success");
        setModalConfig(undefined);
      } else {
        snackbar.show(res.responseMessage, "error");
      }
    }).catch(() => {
      snackbar.show(`Gagal ${isUpdate ? "mengubah" : "menambah"} data`, "error");
    }).finally(() => setLoading(false));
  });

  return (
    <Context.Provider
      value={{
        methods,
        params,
        setParams,
        meta,
        setMeta,
        selectedEntryTypes,
        handleSelectEntryType,

        modalConfig,
        setModalConfig,
        detail,
        searchText,
        setSearchText,
        isLoading,
        rangeCodes,

        transactionCodeQuery,
        transactionCodePages,
        isFetching,
        isFetchingNextPage,
        refetchTable,
        fetchNextPage,
        rangeCodeQuery,

        handleDelete,
        onSubmit,
      }}>

      {children}
    </Context.Provider>
  );
};
